// Tournaments (battle design 7.3, phase 7): three tiers on a fixed calendar, run by the server on their own.
// Pure rules shared by the server (server/tournaments.ts), the client and the tests: tiers and their bans, the
// calendar, entry and prizes, the bracket, bans and orders, and one game.
//
// Format (every tier): open team sheets, bring 6, ban 1 of the opponent's, field 5 in a hidden order, best of 3.
// After each game both orders are shown to both players and both may reorder in secret before the next. A player who
// sends nothing still plays every match: the ban goes on the opponent's strongest creature by Power (Arena) and the
// order is the sheet's order, then the order of the last game. Each game is the Arena's battle (shared/arena.ts) on
// a seed the server draws when the game starts (shared/plan.ts realSeed), so a game replays exactly from its seed,
// the two sheets, the bans and the two orders, and practice runs can never show the real result ahead of time.
//
// Entry is a Tournament Ticket (earned in play: weekly quests, Arena season prizes, tournament prizes; bound to the
// account) or coins. No real money and no diamond entry: prizes are trophies, titles, bound tickets and the top four's
// Aura and Catch Charms (Bag items that trade for coins only, never gems; Cole 2026-10-20), nothing with a cash value
// (tests/tournaments.test.ts checks the rule that a diamond entry needs every prize bound).
import { SPECIES } from './species.ts';
import { arenaPower } from './power.ts';
import type { ArenaCreature, BattleResult } from './arena.ts';
import type { BoardRow } from './board.ts';
import { isRow, pvpBattle } from './pvpfight.ts';
import { capLevels } from './plan.ts';
import { canFight } from './xp.ts';
import { Rng } from './rng.ts';
import { hasRuneTablet } from './tablets.ts';
import { charmItem } from './items.ts';
import type { CreatureDTO } from './protocol.ts';

export { hasRuneTablet };

export type TourTier = 'grand' | 'rising' | 'weekly';

export interface TierDef {
  id: TourTier;
  name: string;
  /** One line for the tier's card. */
  blurb: string;
  /** Trainer level needed to enter. There is never a trainer level maximum: the creature level cap keeps a tier fair. */
  minLevel: number;
  bans: { aura: boolean; bloodlineV: boolean; legendary: boolean; tablets: boolean };
  /**
   * Creatures fight at this level at most; absent (the Grand Championship), at full level. A capped tier's cap equals
   * its minimum trainer level, so every entrant can field a whole team at the cap and longer play stops counting there.
   */
  levelCap?: number;
  /** Entry: Tournament Tickets, or coins, or both as alternatives (0 = not offered). Diamonds are never offered. */
  entry: { tickets: number; coins: number; diamonds: number };
  /** When it runs, in words. */
  when: string;
  /** Sign-ups open this long before the start and close at the start. */
  signupMs: number;
}

const DAY = 86_400_000;

export const TOUR_TIERS: Record<TourTier, TierDef> = {
  grand: {
    id: 'grand', name: 'Grand Championship', blurb: 'The top event of the month. Everything is allowed, at full level: Aura, Bloodline V, legendaries and Rune Tablets.',
    minLevel: 300, bans: { aura: false, bloodlineV: false, legendary: false, tablets: false },
    entry: { tickets: 1, coins: 0, diamonds: 0 }, when: 'the last day of each month', signupMs: 7 * DAY,
  },
  rising: {
    id: 'rising', name: 'Rising Cup', blurb: 'Mid-month: no Aura, Bloodline V or legendaries, and every creature fights at Lv 300 at most.',
    minLevel: 300, bans: { aura: true, bloodlineV: true, legendary: true, tablets: false }, levelCap: 300,
    entry: { tickets: 1, coins: 1_000_000, diamonds: 0 }, when: 'the 15th of each month', signupMs: 7 * DAY,
  },
  weekly: {
    id: 'weekly', name: 'Weekly Clash', blurb: 'Every week: no Aura, Bloodline V, legendaries or Rune Tablets, and every creature fights at Lv 100 at most.',
    minLevel: 100, bans: { aura: true, bloodlineV: true, legendary: true, tablets: true }, levelCap: 100,
    entry: { tickets: 1, coins: 200_000, diamonds: 0 }, when: 'every Saturday', signupMs: 6 * DAY,
  },
};
export const TIER_IDS: TourTier[] = ['grand', 'rising', 'weekly'];

/**
 * Days after launch day before a tier's first event, so players can reach its level first: the Weekly Clash a week
 * in (Lv 100 is reachable in a week), the Rising Cup and the Grand Championship once there are trainers for them.
 * Launch day is set by an admin (server/tournaments.ts); unset, every tier follows the calendar from now.
 */
export const OPEN_AFTER_DAYS: Record<TourTier, number> = { weekly: 7, rising: 21, grand: 35 };

/** The trainer level a tier needs, in words: "Lv 100+". */
export const levelRange = (def: TierDef) => `Lv ${def.minLevel}+`;
/** Whether a trainer level may enter the tier. */
export const levelFits = (def: TierDef, level: number) => level >= def.minLevel;
export const isTier = (x: unknown): x is TourTier => typeof x === 'string' && x in TOUR_TIERS;

/**
 * Server config. While `tournamentsEnabled` is false (the default) the page shows every tier as coming soon and the
 * server never opens sign-ups, never takes an entry fee and never runs an event. An admin turns it on with
 * { t: 'admTournaments', on: true } (kept in the database, so it survives a restart).
 */
export const TOUR_CONFIG = { tournamentsEnabled: false };

export const TOUR = {
  /** Creatures on a sheet, creatures fielded, games to win a match. */
  sheet: 6,
  field: 5,
  winsNeeded: 2,
  /** Every tournament starts at this hour (UTC). */
  hourUtc: 18,
  /** From a round's start: bans close and game 1 starts after banMs; each further game gameGapMs later. */
  banMs: 10 * 60_000,
  gameGapMs: 10 * 60_000,
  /** A round lasts this long; the next round's games start one roundMs later. */
  roundMs: 40 * 60_000,
  /** Fewer entrants than this at the start: cancelled, every entry refunded. */
  minEntrants: 2,
  /** At most this many entrants (8 rounds). */
  maxEntrants: 256,
  /** Games run per server tick at most when catching up, so the hunt keeps ticking. */
  gamesPerTick: 40,
} as const;

// ---------- the calendar ----------
/** The start of the tier's event in the cycle containing `t`'s date (UTC). */
function startInCycle(tier: TourTier, t: number): number {
  const d = new Date(t);
  const y = d.getUTCFullYear(), m = d.getUTCMonth();
  if (tier === 'grand') return Date.UTC(y, m + 1, 0, TOUR.hourUtc); // day 0 of next month = last day of this one
  if (tier === 'rising') return Date.UTC(y, m, 15, TOUR.hourUtc);
  // Weekly: the Saturday of t's week (Sunday-based), 18:00.
  const day = Date.UTC(y, m, d.getUTCDate(), TOUR.hourUtc);
  return day + (6 - d.getUTCDay()) * DAY;
}

/** The first start of this tier at or after `now`. */
export function nextStart(tier: TourTier, now: number): number {
  let s = startInCycle(tier, now);
  if (s >= now) return s;
  if (tier === 'weekly') return s + 7 * DAY;
  const d = new Date(now);
  s = startInCycle(tier, Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1));
  return s;
}

/** The first start of this tier at or after `now`, and never before its opening after launch day (when one is set). */
export function nextStartFrom(tier: TourTier, now: number, launch: number | null): number {
  return nextStart(tier, launch == null ? now : Math.max(now, launch + OPEN_AFTER_DAYS[tier] * DAY));
}

/** A tournament's id: its tier and start date. */
export const tourId = (tier: TourTier, start: number) => `${tier}-${new Date(start).toISOString().slice(0, 10)}`;
export const signupOpensAt = (tier: TourTier, start: number) => start - TOUR_TIERS[tier].signupMs;

/** When round r starts, and when game g of round r is played. */
export const roundStart = (start: number, r: number) => start + r * TOUR.roundMs;
export const gameAt = (start: number, r: number, g: number) => roundStart(start, r) + TOUR.banMs + g * TOUR.gameGapMs;

// ---------- sheets ----------
export const isLegendary = (species: string) => SPECIES[species]?.rarity === 'legendary';
/** Bloodline index 4 is Bloodline V (shared/config.ts). */
export const BLOODLINE_V = 4;

/** Why a creature can't be on this tier's sheet, or null. */
export function creatureBan(tier: TourTier, c: CreatureDTO): string | null {
  const b = TOUR_TIERS[tier].bans;
  const name = SPECIES[c.species]?.name ?? c.species;
  if (b.aura && c.rolls.aura) return `${name} has an Aura, which the ${TOUR_TIERS[tier].name} doesn't allow.`;
  if (b.bloodlineV && c.rolls.bloodline >= BLOODLINE_V) return `${name} is Bloodline V, which the ${TOUR_TIERS[tier].name} doesn't allow.`;
  if (b.legendary && isLegendary(c.species)) return `${name} is a legendary, which the ${TOUR_TIERS[tier].name} doesn't allow.`;
  if (b.tablets && hasRuneTablet(c)) return `${name} carries a Rune Tablet, which the ${TOUR_TIERS[tier].name} doesn't allow.`;
  return null;
}

/** Why one creature can't stand on this tier's sheet for a trainer of this level, or null. */
export function creatureProblem(tier: TourTier, trainerLevel: number, c: CreatureDTO): string | null {
  if (c.listed) return `${SPECIES[c.species]?.name ?? c.species} is listed on the market.`;
  // A creature fights at the tier's cap at most, so one above you still enters when its capped level is within reach.
  if (!canFight({ ...c, level: Math.min(c.level, TOUR_TIERS[tier].levelCap ?? Infinity) }, trainerLevel)) return `${SPECIES[c.species]?.name ?? c.species} is more than 5 levels above you and can't fight yet.`;
  return creatureBan(tier, c);
}

/** Why a trainer can't enter with this sheet, or null. `team`: the creatures in sheet order (undefined = not yours). */
export function sheetProblem(tier: TourTier, trainerLevel: number, team: (CreatureDTO | undefined)[]): string | null {
  const def = TOUR_TIERS[tier];
  if (trainerLevel < def.minLevel) return `The ${def.name} needs trainer level ${def.minLevel}.`;
  if (team.length !== TOUR.sheet) return `Bring exactly ${TOUR.sheet} creatures.`;
  if (team.some((c) => !c || c.listed)) return 'Pick creatures you own that are not listed on the market.';
  const seen = new Set<string>();
  for (const c of team as CreatureDTO[]) {
    if (seen.has(c.species)) return `${SPECIES[c.species]?.name ?? c.species} is on your team twice. Each species once.`;
    seen.add(c.species);
    const why = creatureProblem(tier, trainerLevel, c);
    if (why) return why;
  }
  return null;
}

/** A creature as it goes on a tier's sheet: its fight snapshot, its level capped at the tier's cap. */
export function sheetCreature(tier: TourTier, c: CreatureDTO): ArenaCreature {
  const a: ArenaCreature = { id: c.id, species: c.species, level: c.level, rolls: c.rolls, stars: c.stars, relic: c.relic, ...(c.refine ? { refine: c.refine } : {}), ...(c.tablet ? { tablet: c.tablet } : {}), ...(c.held ? { held: c.held } : {}) };
  return capLevels([a], TOUR_TIERS[tier].levelCap)[0];
}

export const sheetPower = (sheet: ArenaCreature[]) => sheet.reduce((n, c) => n + arenaPower(c), 0);

// ---------- entry and prizes ----------
/**
 * charms: hours of Aura and Catch Charm, paid as 1 h charm items (shared/items.ts charmItem), the only odds items
 * outside ranked. bound: everything the place pays stays on the account (false once it pays charms, which trade).
 */
export interface TourPrize { place: 1 | 2 | 3; trophy: 'champion' | 'finalist' | 'semifinalist'; title: boolean; tickets: number; charms: { aura: number; catch: number }; bound: boolean; cashValue: 0 }
/**
 * 1: the champion, 2: the finalist, 3: both losing semi-finalists. Trophies and titles are cosmetic; tickets are bound.
 * Charms (2026-10-20): a few hours an event against ranked's 3 h a week a player, so the PvP pull grows while Aura
 * supply stays inside its ceiling (only four trainers an event get any).
 */
export const TOUR_PRIZES: TourPrize[] = [
  { place: 1, trophy: 'champion', title: true, tickets: 2, charms: { aura: 3, catch: 0 }, bound: false, cashValue: 0 },
  { place: 2, trophy: 'finalist', title: true, tickets: 1, charms: { aura: 2, catch: 0 }, bound: false, cashValue: 0 },
  { place: 3, trophy: 'semifinalist', title: false, tickets: 0, charms: { aura: 0, catch: 2 }, bound: false, cashValue: 0 },
];
export const prizeFor = (place: number) => TOUR_PRIZES.find((p) => p.place === place) ?? null;
/** The Bag items a place's charms pay: 1 h charms, Aura then Catch. */
export const prizeCharms = (p: TourPrize): { item: string; qty: number }[] =>
  ([['aura', p.charms.aura], ['catch', p.charms.catch]] as const).filter(([, h]) => h > 0).map(([k, h]) => ({ item: charmItem(k, 1), qty: h }));
/** The title a place earns, e.g. "Rising Cup Champion". */
export const titleFor = (tier: TourTier, place: 1 | 2) => `${TOUR_TIERS[tier].name} ${place === 1 ? 'Champion' : 'Finalist'}`;

/**
 * The standing rule on entry: no real money anywhere, and a diamond entry only when every prize is cosmetic or bound
 * to the account (nothing tradeable, no coins, no creatures).
 */
export function entryRuleOk(def: TierDef, prizes: TourPrize[] = TOUR_PRIZES): boolean {
  if (def.entry.diamonds > 0) return prizes.every((p) => p.bound && p.cashValue === 0);
  return true;
}

/** Where tickets come from besides tournament prizes: the weekly quest chest (shared/quests.ts CHEST), and an Arena season top 20. */
export const TICKET_SOURCES = { weeklyQuests: 1, arenaTop: 20, arenaTickets: 1 } as const;
/** Tickets an Arena season rank earns. */
export const arenaTickets = (rank: number) => (rank >= 1 && rank <= TICKET_SOURCES.arenaTop ? TICKET_SOURCES.arenaTickets : 0);

/** A trophy on the trainer: which tournament, its tier, the place and when. */
export interface Trophy { tour: string; tier: TourTier; place: 1 | 2 | 3; at: number }

// ---------- the bracket ----------
/** Seed order for a bracket of n (a power of two): 1 v n, then n/2 v n/2+1 meets them, and so on. */
export function seedOrder(n: number): number[] {
  let s = [1];
  while (s.length < n) { const m = s.length * 2 + 1; s = s.flatMap((x) => [x, m - x]); }
  return s;
}

export const bracketSize = (entrants: number) => { let n = 2; while (n < entrants) n *= 2; return n; };
export const roundsFor = (entrants: number) => Math.log2(bracketSize(entrants));

/**
 * Round-one pairs: entrants ranked by sheet Power (ties: the earlier entry, then the lower account), placed by seed
 * order. Side 0 is the higher seed; a missing opponent (null) is a bye. Returns the pairs and each account's seed.
 */
export function firstRound<T extends { account: number; power: number; at: number }>(entries: T[]): { pairs: [number, number | null][]; seeds: Map<number, number> } {
  const ranked = [...entries].sort((a, b) => b.power - a.power || a.at - b.at || a.account - b.account);
  const n = bracketSize(ranked.length);
  const order = seedOrder(n);
  const seeds = new Map(ranked.map((e, i) => [e.account, i + 1]));
  const at = (s: number) => ranked[s - 1]?.account ?? null;
  const pairs: [number, number | null][] = [];
  for (let i = 0; i < n; i += 2) {
    const x = at(order[i]), y = at(order[i + 1]);
    if (x === null && y === null) continue;
    pairs.push(x === null ? [y!, null] : [x, y]);
  }
  return { pairs, seeds };
}

// ---------- a match ----------
/**
 * The battle rules version a game was played on. A replay rebuilds a game from its seed, so it only matches on the
 * same rules: 2 = battle consistency (2026-10-15: no crits, fixed openings); 3 = the retune the same day (PvP crits
 * 10% at ×2, PvP chart ×1.1 / ×0.9); 4 = 3 with synergies (shared/synergy.ts); 5 = 4 with held items (shared/held.ts,
 * season one); 6 = 3 with held items (synergies switched off, held items on). 7 to 10 are 5, 6, 4 and 3 on the board
 * (shared/board.ts, board rules 1). 11 to 14 are the same four on board rules 2: synergies and held items scaled for
 * the board, and an Awakened move keeps its own cooldown there (board.md "Board scaling"). 15 to 18 are the same four
 * on board rules 3: each creature's fight level swings ±10% per fight (2026-10-20). 19 to 22 are the same four on board
 * rules 4: an element trait counts for the creature's own element (shared/traits.ts ownTrait) and relics count at
 * their cube root (shared/pvp.ts PVP.relicExp), both 2026-10-20. Up to 22, games off the board counted relics in full;
 * 23 to 26 are 3 to 6 with relics at their cube root. Games without `v` were played on 1. Games are played and
 * recorded on the rules the admin switches allow (gameRules).
 */
export const BATTLE_RULES = 19;
/** The oldest rules a replay can rebuild blow for blow: 3 to 26 all still play. */
export const REPLAYABLE_FROM = 3;
/**
 * boardV: the board rules version (shared/board.ts BOARD_RULES) of a board game, which also sets its relic power.
 * relicExp: fronts in order only, 1 for relics in full (rules 3-6); absent, PVP.relicExp.
 */
export interface GameRules { synergy: boolean; held: boolean; board: boolean; boardV?: number; relicExp?: number }
const RULES: Record<number, GameRules> = {
  3: { synergy: false, held: false, board: false, relicExp: 1 },
  4: { synergy: true, held: false, board: false, relicExp: 1 },
  5: { synergy: true, held: true, board: false, relicExp: 1 },
  6: { synergy: false, held: true, board: false, relicExp: 1 },
  7: { synergy: true, held: true, board: true, boardV: 1 },
  8: { synergy: false, held: true, board: true, boardV: 1 },
  9: { synergy: true, held: false, board: true, boardV: 1 },
  10: { synergy: false, held: false, board: true, boardV: 1 },
  11: { synergy: true, held: true, board: true, boardV: 2 },
  12: { synergy: false, held: true, board: true, boardV: 2 },
  13: { synergy: true, held: false, board: true, boardV: 2 },
  14: { synergy: false, held: false, board: true, boardV: 2 },
  15: { synergy: true, held: true, board: true, boardV: 3 },
  16: { synergy: false, held: true, board: true, boardV: 3 },
  17: { synergy: true, held: false, board: true, boardV: 3 },
  18: { synergy: false, held: false, board: true, boardV: 3 },
  19: { synergy: true, held: true, board: true, boardV: 4 },
  20: { synergy: false, held: true, board: true, boardV: 4 },
  21: { synergy: true, held: false, board: true, boardV: 4 },
  22: { synergy: false, held: false, board: true, boardV: 4 },
  23: { synergy: false, held: false, board: false },
  24: { synergy: true, held: false, board: false },
  25: { synergy: true, held: true, board: false },
  26: { synergy: false, held: true, board: false },
};
/** What rules version `v` turns on: synergies, held items and the board (versions before 3: none of them). */
export const rulesOf = (v: number): GameRules => RULES[v] ?? { synergy: false, held: false, board: false };
/** The rules a game starting now is played on, by the synergy, held-item and board switches: the newest that match. */
export function gameRules(synergyOn: boolean, heldOn: boolean, boardOn: boolean): number {
  const vs = Object.keys(RULES).map(Number).sort((a, b) => b - a);
  return vs.find((v) => { const r = RULES[v]; return r.synergy === synergyOn && r.held === heldOn && r.board === boardOn; })!;
}
/**
 * One game: the two orders that fought (creature ids), the seed, the result, the rules version (v), and on the board
 * each side's rows as they stood (rows[s][k] is orders[s][k]'s row, defaultRows filled in), so a replay needs nothing else.
 */
export interface GameRec { seed: number; orders: [number[], number[]]; winner: 0 | 1; ms: number; timedOut: boolean; v?: number; rows?: [BoardRow[], BoardRow[]] }
/**
 * A match: bans[s] is the creature side s banned from the other side's sheet; orders[s] a pending reorder, and rows[s]
 * the rows sent with it (front or back per id of orders[s]; the board).
 */
export interface MatchState { bans: [number | null, number | null]; orders: [number[] | null, number[] | null]; games: GameRec[]; rows?: [BoardRow[] | null, BoardRow[] | null] }
export const freshMatch = (): MatchState => ({ bans: [null, null], orders: [null, null], games: [] });

export const winsOf = (m: Pick<MatchState, 'games'>): [number, number] => [m.games.filter((g) => g.winner === 0).length, m.games.filter((g) => g.winner === 1).length];
/** The side that took the match, or null while it runs. */
export function matchWinner(m: Pick<MatchState, 'games'>): 0 | 1 | null {
  const [a, b] = winsOf(m);
  return a >= TOUR.winsNeeded ? 0 : b >= TOUR.winsNeeded ? 1 : null;
}

/** The auto-ban: the opponent's strongest creature by Power (Arena), the earlier one on a tie. */
export function autoBan(theirSheet: ArenaCreature[]): number {
  let best = theirSheet[0];
  for (const c of theirSheet) if (arenaPower(c) > arenaPower(best)) best = c;
  return best.id;
}

/** The creatures side s may field: its sheet without the one the other side banned. */
export const available = (sheet: ArenaCreature[], bannedByOther: number | null) => sheet.filter((c) => c.id !== bannedByOther);
/** How many creatures a side fields. */
export const fieldSize = (sheet: ArenaCreature[], bannedByOther: number | null) => Math.min(TOUR.field, available(sheet, bannedByOther).length);

/** The creatures an order fields: its ids that are on the sheet and not banned, the first five. */
export function fieldFrom(ids: readonly number[], sheet: ArenaCreature[], bannedByOther: number | null): number[] {
  const ok = new Set(available(sheet, bannedByOther).map((c) => c.id));
  return ids.filter((id) => ok.has(id)).slice(0, TOUR.field);
}

/**
 * Why a sent order can't be used, or null. Before the bans lock the opponent's ban is unknown, so an order ranks the
 * whole sheet (the banned one drops out and the first five fight); once bans lock it names exactly the creatures to
 * field.
 */
export function orderProblem(ids: unknown, sheet: ArenaCreature[], bannedByOther: number | null, locked: boolean): string | null {
  if (!Array.isArray(ids) || !ids.every((x) => Number.isInteger(x))) return 'Send an order of creature ids.';
  if (new Set(ids).size !== ids.length) return 'A creature is in the order twice.';
  if (ids.some((id) => !sheet.some((c) => c.id === id))) return 'That creature is not on your team.';
  if (!locked) return ids.length === sheet.length ? null : `Rank all ${sheet.length} creatures on your team; the one your opponent bans drops out.`;
  if (ids.includes(bannedByOther)) return 'That creature is banned for this match.';
  const want = fieldSize(sheet, bannedByOther);
  return ids.length === want ? null : `Field exactly ${want} creatures.`;
}

/**
 * The order side s fields in its next game: its pending reorder, else the order of its last game, else its sheet
 * order (the first five that are available). Idle players always have one.
 */
export function orderFor(m: MatchState, s: 0 | 1, sheet: ArenaCreature[]): number[] {
  const banned = m.bans[(1 - s) as 0 | 1];
  const want = fieldSize(sheet, banned);
  for (const ids of [m.orders[s], m.games[m.games.length - 1]?.orders[s], sheet.map((c) => c.id)]) {
    if (!ids) continue;
    const f = fieldFrom(ids, sheet, banned);
    if (f.length === want) return f;
  }
  return fieldFrom(sheet.map((c) => c.id), sheet, banned);
}

/**
 * The rows side s fields `order` on next game (the board): the rows it sent with its pending reorder, else the rows of
 * its last game, else the rows it entered its sheet with, whichever covers every creature of the order first.
 * Undefined: defaultRows.
 */
export function nextRows(m: Pick<MatchState, 'orders' | 'rows' | 'games'>, s: 0 | 1, order: readonly number[], entry?: { ids: readonly number[]; rows?: readonly BoardRow[] | null } | null): BoardRow[] | undefined {
  const last = m.games[m.games.length - 1];
  const sources = [
    m.orders[s] && m.rows?.[s] ? { ids: m.orders[s]!, rows: m.rows[s]! } : null,
    last?.rows ? { ids: last.orders[s], rows: last.rows[s] } : null,
    entry?.rows ? { ids: entry.ids, rows: entry.rows } : null,
  ];
  for (const src of sources) {
    if (!src) continue;
    const r = order.map((id) => { const i = src.ids.indexOf(id); return i >= 0 ? src.rows[i] : undefined; });
    if (r.every(isRow)) return r as BoardRow[];
  }
  return undefined;
}

/**
 * One game, as the server plays it and every replay repeats it: each side fields its order from its sheet
 * (shared/pvpfight.ts pvpBattle), on rules version `v` (rulesOf: synergies, held items, the board; never a ranked
 * season's theme rule), on the board with `rows` (rows[s][k] for orders[s][k]; absent: defaultRows). The higher seed
 * (match side 0) defends, so it keeps a dead tie. The result is given in match sides. Pure: the same sheets, orders,
 * rows, seed and rules give the same game.
 */
export function playGame(names: [string, string], sheets: [ArenaCreature[], ArenaCreature[]], orders: [number[], number[]], seed: number, v = BATTLE_RULES, rows?: [(BoardRow[] | undefined)?, (BoardRow[] | undefined)?]): BattleResult {
  const pick = (s: 0 | 1) => {
    const k = orders[s].map((id, i) => ({ c: sheets[s].find((c) => c.id === id), i })).filter((x): x is { c: ArenaCreature; i: number } => !!x.c);
    return { team: k.map((x) => x.c), rows: rows?.[s] ? k.map((x) => rows[s]![x.i]) : undefined };
  };
  const p0 = pick(0), p1 = pick(1);
  const res = pvpBattle({ name: names[1], ...p1 }, { name: names[0], ...p0 }, new Rng(seed), rulesOf(v));
  return {
    ...res, sides: [res.sides[1], res.sides[0]], winner: (1 - res.winner) as 0 | 1, log: res.log.map((b) => ({ ...b, s: (1 - b.s) as 0 | 1 })),
    ...(res.synergy ? { synergy: [res.synergy[1], res.synergy[0]] as BattleResult['synergy'] } : {}),
  };
}

// ---------- messages ----------
/** rows: front or back per id, as the sheet was entered (the board); absent: defaultRows. */
export interface TourEntryView { ids: number[]; pay: 'ticket' | 'coins'; fee: number; rows?: BoardRow[] }
export interface TierCard {
  tier: TourTier;
  /** The next or running event of this tier. */
  id: string; start: number; opensAt: number; state: 'open' | 'running' | 'done' | 'cancelled';
  entrants: number;
  mine: TourEntryView | null;
  /** The last finished event of this tier and its champion. */
  last: { id: string; champion: string } | null;
}
export interface BracketMatch { round: number; slot: number; names: [string, string | null]; wins: [number, number]; winner: 0 | 1 | null; titles?: [string | null, string | null] }
export interface MyMatch {
  tour: string; round: number; slot: number;
  /** Your side (0: the higher seed). */
  side: 0 | 1;
  names: [string, string];
  /** Both sheets, open: full rolls, in sheet order. */
  sheets: [ArenaCreature[], ArenaCreature[]];
  /** Your ban (sent or locked); the other side's only once bans have locked. */
  bans: [number | null, number | null];
  bansLocked: boolean;
  /** Your pending reorder for the next game (only yours is ever shown). */
  myOrder: number[] | null;
  /** The order you will field next if you send nothing, and its rows on the board (absent: defaultRows). */
  nextOrder: number[];
  nextRows?: BoardRow[];
  games: GameRec[];
  /** When the next game starts (null once decided). */
  nextAt: number | null;
  winner: 0 | 1 | null;
}
export interface TourView {
  id: string; tier: TourTier; start: number; state: 'open' | 'running' | 'done' | 'cancelled';
  rounds: number; entrants: number;
  bracket: BracketMatch[];
  champion: string | null;
  /** Your match now (or your last one in this event). */
  my: MyMatch | null;
}
export interface TourDTO {
  /** False: tournaments are coming soon (no sign-ups, no events). */
  enabled: boolean;
  /** Launch day (UTC midnight) when an admin has set it: each tier's first event waits OPEN_AFTER_DAYS from it. */
  launch: number | null;
  cards: TierCard[];
  /** The event you are in now, else the latest one that ran. */
  view: TourView | null;
  tickets: number;
  trophies: Trophy[];
  title: string | null;
}
